import { useCallback, useEffect, useState } from "react";
import { usePdfViewerInteractionContext } from "@/src/components/pdf-viewer/interaction-provider";
import { Label } from "@/src/components/shadcn/ui/label";
import { Select, SelectTrigger, SelectValue } from "@/src/components/shadcn/ui/select";
import { SelectWrapper } from "@/src/components/tasks/input-wrapper";
import { logger } from "@/src/lib/logging/logger";
interface ServiceLineSelectorProps {
  value: string | undefined | null;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  isRequired?: boolean;
  onFlagForInaccuracyUpdate?: (key: string, value: any) => void;
  fieldKey?: string;
  metadata?: any;
  disabled?: boolean;
}
interface ServiceLineOption {
  value: string;
  label: string;
}
export default function ServiceLineSelector({
  value,
  onChange,
  label = "Service Line",
  placeholder,
  isRequired = false,
  onFlagForInaccuracyUpdate,
  fieldKey = "serviceLine",
  metadata,
  disabled = false
}: ServiceLineSelectorProps) {
  const {
    setMetadata,
    clearMetadata
  } = usePdfViewerInteractionContext();
  const [serviceLineOptions, setServiceLineOptions] = useState<ServiceLineOption[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let isActive = true;
    const fetchServiceLines = async () => {
      try {
        const response = await fetch(`/api/service-lines`);
        if (!response.ok) {
          throw new Error(`Failed to fetch service lines: ${response.statusText}`);
        }
        const data = await response.json();
        if (isActive) {
          setServiceLineOptions(data);
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Failed to fetch service lines";
        logger.error("Failed to fetch service lines:", {
          error: err
        });
        if (isActive) {
          setError(errorMessage);
          setServiceLineOptions([]);
        }
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };
    void fetchServiceLines();
    return () => {
      isActive = false;
    };
  }, []);
  const handleFocus = useCallback(() => {
    setMetadata(metadata?.serviceLine_metadata);
  }, [setMetadata, metadata]);
  const handleBlur = useCallback(() => {
    clearMetadata();
  }, [clearMetadata]);

  // Show loading state or error message if needed
  if (isLoading) {
    return <div className="px-1">
        <div className="mb-2">
          {label && <div className="flex items-center justify-between">
              <Label className="text-muted-foreground">{label}</Label>
            </div>}
        </div>
        <Select disabled>
          <SelectTrigger className="bg-input">
            <SelectValue placeholder="Loading service lines..." />
          </SelectTrigger>
        </Select>
      </div>;
  }
  if (error) {
    return <div className="px-1">
        <div className="mb-2">{label && <div className="text-muted-foreground">{label}</div>}</div>
        <div className="bg-input px-3 py-2 rounded-md text-sm text-destructive">Error: {error}</div>
      </div>;
  }
  return <SelectWrapper value={value} onChange={onChange} onFocus={handleFocus} onBlur={handleBlur} label={label} placeholder={placeholder} isRequired={isRequired} disabled={disabled} options={serviceLineOptions} onFlagForInaccuracyUpdate={onFlagForInaccuracyUpdate} fieldKey={fieldKey} />;
}